import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Platform } from '../../../model/platform/Platform';
import { initialPlatform } from '../dto/initialPlatform';
import { PlatformService } from '../PlatformService';
import { ErrorToast, SuccessToast, WarningToast } from '../../../utils/toasters';
import { Button, Col, Form, Image, Row } from 'react-bootstrap';
import { multipartFormData } from '../../../api/api.service';
import { PLATFORM_URL } from '../../../utils/Endpoints';
import { useNavigate } from 'react-router-dom';
import CardLayout from '../../../shared/components/CardLayout';
import SectionTitle from '../../../shared/components/SectionTitle';
import ToggleChip from '../../../shared/components/ToggleChip';

const METRIC_FIELDS: { label: string; key: keyof Platform }[] = [
    { label: 'Avg Likes', key: 'averageLikes' },
    { label: 'Avg Views', key: 'averageViews' },
    { label: 'Avg Comments', key: 'averageComments' },
    { label: 'Avg Reel Plays', key: 'averageReelPlays' },
    { label: 'Total Posts', key: 'totalPosts' },
    { label: 'Followers', key: 'followers' },
    { label: 'Subscribers', key: 'subscribers' },
    { label: 'Total Watch Time', key: 'totalWatchTime' },
    { label: 'Unique Visitors', key: 'uniqueVisitors' },
    { label: 'Total Page Views', key: 'totalPageViews' },
    { label: 'Newsletter Subscribers', key: 'newsletterSubscribers' },
    { label: 'Average Downloads', key: 'averageDownloads' },
    { label: 'Average Plays', key: 'averagePlays' },
    { label: 'Total Episodes', key: 'totalEpisodes' },
    { label: 'Avg Concurrent Viewers', key: 'averageConcurrentViewers' },
    { label: 'View Minutes', key: 'viewMinutes' },
    { label: 'Avg Post Engagements', key: 'averagePostEngagements' },
    { label: 'Avg Post Impressions', key: 'averagePostImpressions' },
    { label: 'Platforms', key: 'allPlatforms' },
    { label: 'Engagement Rate', key: 'engagementRate' },
    { label: 'Live Hours Per Month', key: 'liveHoursPerMonth' },
];

export function PlatformForm() {
    const { platformId } = useParams<{ platformId: string }>();
    const [selectedFileLight, setSelectedFileLight] = useState(null);
    const [selectedFileDark, setSelectedFileDark] = useState(null);
    const [imagePath, setImage] = useState({
        lightPath: '',
        darkPath: '',
    });
    const [platform, setPlatform] = useState<Platform>(initialPlatform);
    const navigate = useNavigate();

    useEffect(() => {
        if (platformId) {
            PlatformService.getPlatform(+platformId)
                .then(response => {
                    setPlatform(response);
                    if (response.path) {
                        setImage({
                            lightPath: response.path,
                            darkPath: response?.darkPath ?? '',
                        });
                    }
                })
                .catch(err => ErrorToast(err));
        } else {
            PlatformService.getPlatforms({ name: '', page: 1, perPage: 1000 })
                .then(response => {
                    const maxPosition = Math.max(0, ...response.data.map(item => item.position ?? 0));
                    setPlatform(prev => ({ ...prev, position: maxPosition + 1 }));
                })
                .catch(err => ErrorToast(err));
        }
    }, []);

    const updatePlatform = () => {
        if (selectedFileDark) {
            multipartFormData(selectedFileDark, `${PLATFORM_URL}/dark/${platformId}`, JSON.stringify({}), 'post')
                .then(response => response)
                .catch(err => ErrorToast(err));
        }
        multipartFormData(selectedFileLight, `${PLATFORM_URL}/${platformId}`, JSON.stringify(platform))
            .then(() => {
                SuccessToast('Successfully updated platform');
                navigate('/platform/platforms');
            })
            .catch(err => ErrorToast(err));
    };

    const createPlatform = () => {
        if (platform.position !== null && platform.name !== '' && selectedFileLight !== null) {
            multipartFormData(selectedFileLight, PLATFORM_URL, JSON.stringify(platform), 'post')
                .then(response => {
                    if (selectedFileDark !== null && response?.data?.id) {
                        multipartFormData(
                            selectedFileDark,
                            `${PLATFORM_URL}/dark/${response.data.id}`,
                            JSON.stringify({}),
                            'post'
                        )
                            .then(response => response)
                            .catch(err => ErrorToast(err));
                    }
                    SuccessToast('Successfully created platform');
                    navigate('/platform/platforms');
                })
                .catch(err => ErrorToast(err));
        } else {
            WarningToast('Please fill out all mandatory fields');
        }
    };

    function handleChange(event: any, isNumber = false) {
        const value = isNumber ? +event.target.value : event.target.value;
        setPlatform({
            ...platform,
            [event.target.name]: value,
        });
    }

    function handleCheckbox(evt: any) {
        const change = evt.target.checked;
        setPlatform({
            ...platform,
            [evt.target.name]: change,
        });
    }

    function handleFileInput(evt: any, isLight = false) {
        if (evt.target.files.length > 0) {
            if (isLight) {
                setSelectedFileLight(evt.target.files[0]);
            } else {
                setSelectedFileDark(evt.target.files[0]);
            }
            setImage(prev => ({
                ...prev,
                [isLight ? 'lightPath' : 'darkPath']: URL.createObjectURL(evt.target.files[0]),
            }));
        }
    }

    return (
        <CardLayout>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h4 className="fw-bold mb-0">{platformId ? 'Update platform' : 'Create platform'}</h4>
                <Button
                    onClick={platformId ? updatePlatform : createPlatform}
                    variant="primary"
                    size="sm"
                    className="px-4"
                >
                    Save
                </Button>
            </div>
            <hr className="mb-4" />

            <Form>
                <Row>
                    <Col md={8} className="pr-8">
                        <SectionTitle title="General" />
                        <Row className="mb-2">
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">Position</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="position"
                                    onChange={event => handleChange(event, true)}
                                    value={platform?.position}
                                />
                            </Form.Group>
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="required fw-semibold">Name</Form.Label>
                                <Form.Control name="name" onChange={handleChange} value={platform?.name} />
                            </Form.Group>
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="fw-semibold">Mobile identify</Form.Label>
                                <Form.Control
                                    name="mobileIdentify"
                                    onChange={event => handleChange(event)}
                                    value={platform?.mobileIdentify}
                                />
                            </Form.Group>
                            <Form.Group className="col-6 mb-3">
                                <Form.Label className="fw-semibold">CPM</Form.Label>
                                <Form.Control
                                    name="predefinedCPM"
                                    onChange={handleChange}
                                    value={platform?.predefinedCPM}
                                />
                            </Form.Group>
                            {platform.hasApi && (
                                <Form.Group className="col-6 mb-3">
                                    <Form.Label className="fw-semibold">
                                        Total members{' '}
                                        <span className="text-muted fw-normal">(displayed on discovery)</span>
                                    </Form.Label>
                                    <Form.Control
                                        name="totalMembers"
                                        onChange={handleChange}
                                        value={platform?.totalMembers}
                                    />
                                </Form.Group>
                            )}
                        </Row>

                        <SectionTitle title="Status" />
                        <div className="d-flex flex-wrap gap-3 mb-2">
                            <ToggleChip
                                name="isEnabled"
                                onChange={handleCheckbox}
                                label="Enabled"
                                checked={!!platform?.isEnabled}
                            />
                            <ToggleChip
                                name="hasApi"
                                onChange={handleCheckbox}
                                label="API"
                                checked={!!platform?.hasApi}
                            />
                        </div>

                        <SectionTitle title="Metrics displayed on discovery" />
                        <div className="d-flex flex-wrap gap-3 mb-2">
                            {METRIC_FIELDS.map(({ label, key }) => (
                                <ToggleChip
                                    key={key}
                                    name={key}
                                    onChange={handleCheckbox}
                                    label={label}
                                    checked={!!platform?.[key]}
                                />
                            ))}
                        </div>
                    </Col>
                    <Col md={4}>
                        <SectionTitle title="Light theme" />
                        <Form.Group>
                            <Form.Control
                                accept=".svg, .jpg, .png "
                                type="file"
                                onChange={e => handleFileInput(e, true)}
                            />
                            <div className="d-flex align-items-center justify-content-center border rounded-3 mt-3 p-3 bg-light">
                                <Image
                                    width={140}
                                    height={110}
                                    style={{ objectFit: 'contain' }}
                                    alt={imagePath.lightPath || 'Light theme icon'}
                                    src={imagePath.lightPath || '/images/placeholder.png'}
                                />
                            </div>
                        </Form.Group>

                        <SectionTitle title="Dark theme" />
                        <Form.Group>
                            <Form.Control
                                accept=".svg, .jpg, .png "
                                type="file"
                                onChange={e => handleFileInput(e, false)}
                            />
                            <div className="d-flex align-items-center justify-content-center border rounded-3 mt-3 p-3 bg-dark">
                                <Image
                                    width={140}
                                    height={110}
                                    style={{ objectFit: 'contain' }}
                                    alt={imagePath.darkPath || 'Dark theme icon'}
                                    src={imagePath.darkPath || '/images/placeholder.png'}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
            </Form>
        </CardLayout>
    );
}
